import { useEffect, useState } from "react";
import TopTitleBanner from "../component/TopTitleBanner";
import "../css/dashboard.css";
import "../css/client.css";
import { Box, Button, Menu, MenuItem, Modal } from "@mui/material";
import AddClient from "../component/forms/AddClient";
import { collection, documentId, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { roles } from "../firebase.collection";
import AddQuickNoteWithClients from "../component/Notes/AddQuickNoteWithClients";

import { db } from "../firebase";
import NotesContainer from "../component/Notes/NotesContainer";
import EndOfShiftNoteContainer from "../component/Notes/EndOfShiftNoteContainer";
import FamilyNoteContainer from "../component/Notes/FamilyNoteContainer";
import MedicalNoteContainer from "../component/Notes/MedicalNoteContainer";
import HandoverNoteContainer from "../component/Notes/HandoverNoteContainer";
import GroupNoteContainer from "../component/Notes/GroupNoteContainer";
import { format } from "date-fns";
import MetricsWidgetV2 from "../component/MetricsWidgetV2";
import NotesAddModal from "../component/modals/NotesAddModal";
import { Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export default function HomepageContent() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [, setReadCount] = useState(0);
  const { currentUser } = useAuth();
  const userIdState = currentUser ? currentUser.uid : "";
  const [userRole, setUserRole] = useState("");
  const [, setNoteTypes] = useState([]);
  const [selectedClientName] = useState("");
  const [, setClientNames] = useState([]);
  const [selectedNoteType] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientsIncoming, setClientsIncoming] = useState([]);
  const [nextDischargeClient, setNextDischargeClient] = useState(null);
  const [, setIsLoading] = useState(true);
  const [, setIsLoadingIncoming] = useState(true);
  const [, setFirestoreReadCount] = useState(0);
  const [openAddModal2, setOpenAddModal2] = useState(false);
  const [anchorElNoteType, setAnchorElNoteType] = useState(null);
  const openNoteTypeMenu = Boolean(anchorElNoteType);
  const [currentNoteType, setCurrentNoteType] = useState("");
  const [notesLimit, setNotesLimit] = useState(15); // Limite initiale à 15

  const handleOpenNoteTypeMenu = (event) => {
    setAnchorElNoteType(event.currentTarget);
  };

  const handleCloseNoteTypeMenu = () => {
    setAnchorElNoteType(null);
  };

  const handleSelectNoteType = (noteType) => {
    setCurrentNoteType(noteType);
    setOpenAddModal2(true);
    handleCloseNoteTypeMenu();
  };

  // const handleClientClick = (clientId) => {
  //   navigate(`/client/${clientId}`);
  // };

  const incrementFirestoreReadCount = (incrementBy = 1) => {
    setFirestoreReadCount((prevCount) => prevCount + incrementBy);
  };

  const handleClientClick = (clientId) => {
    window.open(`/client/${clientId}`, "_blank");
  };

  useEffect(() => {
    setIsLoading(true);

    const queryRef = query(
      collection(db, "clients"),
      where("clientStatus", "==", "In House")
    );

    const unsubscribe = onSnapshot(
      queryRef,
      async (snapshot) => {
        const clientsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClients(clientsData);
        setIsLoading(false);
        incrementFirestoreReadCount(snapshot.docs.length);
      },
      (error) => {
        console.error("Failed to fetch In House clients:", error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe(); // Cleanup subscription on unmount
  }, []);

  useEffect(() => {
    setIsLoadingIncoming(true);

    const clientQuery = query(
      collection(db, "clients"),
      where("clientStatus", "==", "Incoming"),
      orderBy("eta", "asc"), // Trie par ETA croissant
      limit(1) // Limite à un seul client
    );

    const unsubscribe = onSnapshot(
      clientQuery,
      async (snapshot) => {
        const clientsIncomingData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClientsIncoming(clientsIncomingData);
        setIsLoadingIncoming(false);
        incrementFirestoreReadCount(snapshot.docs.length);
      },
      (error) => {
        console.error("Failed to fetch Incoming clients:", error);
        setIsLoadingIncoming(false);
      }
    );

    return () => unsubscribe(); // Cleanup subscription on unmount
  }, []);

  useEffect(() => {
    if (clients.length > 0) {
      const now = new Date();
      const closestClient = clients.reduce((closest, client) => {
        const dischargeDate = new Date(
          client.projectedResidentialDischargeDate?.seconds * 1000
        );

        if (isNaN(dischargeDate.getTime()) || dischargeDate <= now) {
          return closest; // Ignore invalid or past dates
        }
        if (
          closest === null ||
          dischargeDate <
            new Date(closest.projectedResidentialDischargeDate?.seconds * 1000)
        ) {
          return client;
        }
        return closest;
      }, null);

      setNextDischargeClient(closestClient);
    }
  }, [clients]);

  function formatDate2(date) {
    if (!date || isNaN(date.getTime())) {
      console.log("Invalid date provided to formatDate");
      return "Invalid date"; // Retourner une chaîne indiquant que la date est invalide
    }
    try {
      return format(date, "dd MMMM yyyy"); // Format correct avec date-fns
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Formatting error";
    }
  }

  useEffect(() => {
    const notesRef = collection(db, "notes");
    // const q = query(notesRef, orderBy("effectiveDateTime", "desc"), limit(25));
    const q = query(
      notesRef,
      orderBy("effectiveDateTime", "desc"),
      limit(notesLimit)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const notesData = [];
      const types = new Set();
      querySnapshot.forEach((doc) => {
        const noteData = { id: doc.id, ...doc.data() };
        notesData.push(noteData);
        types.add(noteData.noteType);
      });
      setNotes(notesData);
      setNoteTypes(Array.from(types));
      setReadCount((prevCount) => prevCount + querySnapshot.size);
      incrementFirestoreReadCount(querySnapshot.size);
    });

    return () => {
      unsubscribe();
    };
  }, [notesLimit]);

  const formatNoteType = (noteType) => {
    return noteType
      .replace(/([A-Z])/g, " $1") // Ajouter un espace avant chaque lettre majuscule
      .replace(/^./, (str) => str.toUpperCase()); // Mettre la première lettre en majuscule
  };

  const fixedNoteTypes = [
    "quickNote",
    "medicalNote",
    "supportStaffNote",
    "groupNote",
    "therapyNote",
    "handoverNote",
    "endOfShift",
    "familyNote",
    "aftercareNote",
  ];

  useEffect(() => {
    if (selectedNoteType.length > 0) {
      const filteredNotesByType = notes.filter((note) =>
        selectedNoteType.includes(note.noteType)
      );
      const uniqueClientNames = Array.from(
        new Set(
          filteredNotesByType.map(
            (note) => note.clientFirstName + " " + note.clientLastName
          )
        )
      );
      setClientNames(uniqueClientNames);
    } else {
      // Si aucun type de note n'est sélectionné, afficher tous les clients
      const allClientNames = Array.from(
        new Set(
          notes.map((note) => note.clientFirstName + " " + note.clientLastName)
        )
      );
      setClientNames(allClientNames);
    }
  }, [selectedNoteType, notes]);

  useEffect(() => {
    if (currentUser) {
      const roleQuery = query(roles, where(documentId(), "==", userIdState));

      const unsubscribe = onSnapshot(roleQuery, (querySnapshot) => {
        const doc = querySnapshot.docs[0];
        if (doc && doc.exists()) {
          setUserRole(doc.data().role);
        } else {
          console.warn("❌ Aucun rôle trouvé pour cet utilisateur.");
        }
      });

      return () => unsubscribe(); // Nettoyage de la souscription
    }
  }, [currentUser]);

  const filteredNotes = userRole
    ? notes
        .filter((note) => {
          const matchesType = selectedNoteType.length
            ? selectedNoteType.includes(note.noteType)
            : true;

          const matchesClient = selectedClientName
            ? `${note.clientFirstName} ${note.clientLastName}`.toLowerCase() ===
              selectedClientName.toLowerCase()
            : true;

          // ✅ Vérifie si l'utilisateur peut voir cette note
          const isVisibleToUser =
            note.noteType !== "therapyNote" || // Toutes les notes sauf therapyNote sont visibles
            userRole === "Super Admin" ||
            userRole === "Clinical Director" ||
            (userRole === "Therapist" &&
              note.doneBy === currentUser.displayName); // Les therapists voient uniquement leurs propres therapyNotes

          // 🔍 LOGGING DES TESTS

          return matchesType && matchesClient && isVisibleToUser;
        })
        .sort((a, b) => {
          return b.effectiveDateTime.toDate() - a.effectiveDateTime.toDate();
        }) // Trie du plus récent au plus ancien
    : []; // 🔴 Si `userRole` est vide, ne retourne rien encore.

  const styleModalGeneral = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "min(760px, calc(100vw - 40px))",
    height: "auto",
    maxHeight: "calc(100vh - 48px)",
    borderRadius: 3,
    // overflowY: "scroll",
    backgroundColor: "transparent",
    /*border: "1px solid #000",*/
    boxShadow: "none",
    outline: "none",
  };

  const styleModalQuickNote = {
    ...styleModalGeneral, // Vous pouvez réutiliser le style de la modal générale
  };

  const styleModalQuickNoteLegacy = {
    ...styleModalQuickNote,
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "40%",
    height: "80%",
    borderRadius: 5,
    backgroundColor: "white",
    boxShadow: 24,
  };

  const [openAddClient, setOpenAddClient] = useState(false);
  const [openAddQuickNote, setOpenAddQuickNote] = useState(false);

  const isPhilippinesClient = (client) =>
    client.rehabId === "miracles_asia_philippines" ||
    client.rehabCountry === "Philippines";

  const activePHClients = clients.filter(isPhilippinesClient);
  const inHouseTHClients = clients.filter((client) => !isPhilippinesClient(client));

  const handleOpenAddClient = () => {
    setOpenAddClient(true);
    // recordMetrics("Clients", "addClient", currentUser.email, "write", "bodhi");
  };

  const handleCloseAddClient = () => setOpenAddClient(false);
  const handleCloseAddQuickNote = () => setOpenAddQuickNote(false);

  const formatDate = (date, options) => {
    return date
      ? new Intl.DateTimeFormat("en-EN", options).format(date.toDate())
      : "";
  };

  return (
    <div className="right-container">
      <NotesAddModal
        open={openAddModal2}
        onClose={() => setOpenAddModal2(false)}
        // onClose={handleCloseAddNoteModal}
        noteType={currentNoteType}
        // handleSave={handleSaveNote}
      />
      <Modal
        open={openAddClient}
        onClose={handleCloseAddClient}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={styleModalGeneral}>
          <AddClient closemodal={handleCloseAddClient} />
        </Box>
      </Modal>
      <Modal
        open={openAddQuickNote}
        onClose={handleCloseAddQuickNote}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={styleModalQuickNoteLegacy}>
          <AddQuickNoteWithClients closemodal={handleCloseAddQuickNote} />
        </Box>
      </Modal>
      <TopTitleBanner title={"Dashboard"} />

      <div
        className="top-component-list-clients"
        style={{ width: "1330px", display: "flex", flexDirection: "column" }}
      >
        <div className="dashboard-summary-toolbar">
          <div
            className="dashboard-summary-card-action"
            onClick={() => navigate("/clients")}
          >
            <MetricsWidgetV2
              title="# Inhouse TH"
              value={inHouseTHClients.length.toString()}
              title2="Clients"
              backgroundColor="#425e8d"
              textColor="white"
            />
          </div>

          <div
            className="dashboard-summary-card-action"
            onClick={() => navigate("/clients?rehab=ph&tab=inhouse")}
          >
            <MetricsWidgetV2
              title="Active PH"
              value={activePHClients.length.toString()}
              title2="Clients"
              backgroundColor="#16865d"
              textColor="white"
            />
          </div>

          <div>
            <MetricsWidgetV2
              title="Next Departure "
              value={
                nextDischargeClient ? (
                  <span
                    className="dashboard-summary-link"
                    onClick={() => handleClientClick(nextDischargeClient.id)}
                  >
                    {`${nextDischargeClient.firstName} ${nextDischargeClient.lastName}`}
                  </span>
                ) : (
                  "No discharges"
                )
              }
              title2={
                nextDischargeClient
                  ? formatDate2(
                      new Date(
                        nextDischargeClient.projectedResidentialDischargeDate
                          ?.seconds * 1000
                      )
                    )
                  : "---"
              }
              backgroundColor="#425e8d"
              textColor="white"
            />
          </div>

          <MetricsWidgetV2
            title="Next Incoming Client "
            value={
              clientsIncoming.length > 0 ? (
                <span
                  className="dashboard-summary-link"
                  onClick={() => handleClientClick(clientsIncoming[0].id)}
                >
                  {`${clientsIncoming[0].firstName} ${clientsIncoming[0].lastName}`}
                </span>
              ) : (
                "No upcoming clients"
              )
            }
            title2={
              clientsIncoming.length > 0
                ? formatDate2(new Date(clientsIncoming[0].eta?.seconds * 1000))
                : "N/A"
            }
            backgroundColor="#425e8d"
            textColor="white"
          />
          {/* <SearchBar /> */}

          <div className="dashboard-summary-actions">
            {(userRole === "Super Admin" || userRole === "Admission staff") && (
              // userRole === "Support Staff" ||
              // userRole === "Clinical Director" ||
              // userRole === "Admin" ||
              // userRole === "Medical Staff" ||
              // userRole === "Therapist"
              <>
                <Button
                  className="dashboard-summary-action-button"
                  startIcon={<Add />}
                  onClick={handleOpenAddClient}
                  // variant="contained"
                  variant="outlined"
                >
                  Add Client
                </Button>
                <Button
                  className="dashboard-summary-action-button dashboard-summary-action-button-secondary"
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenNoteTypeMenu}
                  startIcon={<Add />} // style={{
                  //   width: "150px",
                  //   height: "35px",
                  //   marginRight: "20px",
                  //   fontSize: "12px",
                  //   marginTop: "10px",
                  // }}
                  // onClick={handleOpenAddQuickNote}
                  // variant="contained"
                >
                  Add Note
                </Button>
                <Menu
                  anchorEl={anchorElNoteType}
                  open={openNoteTypeMenu}
                  onClose={handleCloseNoteTypeMenu}
                >
                  {/* {noteTypes.map((noteType) => ( */}
                  {fixedNoteTypes.map((noteType) => (
                    <MenuItem
                      key={noteType}
                      onClick={() => handleSelectNoteType(noteType)}
                    >
                      {formatNoteType(noteType)}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </div>
        </div>

        <div
          style={{
            fontSize: "22px",
            fontWeight: "600",
            marginBottom: "20px",
            marginTop: "10px",
            paddingLeft: "0px",
          }}
        >
          Recent Clients Notes ({filteredNotes.length})
        </div>
      </div>
      <div
        style={{
          // backgroundColor: "yellow",
          width: "100%",
          height: "auto",
          display: "flex",
          flexDirection: "row",
          paddingLeft: "40px",
          paddingRight: "60px",
        }}
      >
        <div
          style={{
            width: "80%",
            maxHeight: "600px",
            overflowY: "auto",

            // backgroundColor: "blue"
          }}
        >
          {/* {filteredNotes.map((note, index) => { */}
          {filteredNotes.slice(0, notesLimit).map((note, index) => {
            const dateOptions = {
              day: "numeric",
              month: "short",
              year: "numeric",
            };

            const dateOptions2 = {
              day: "numeric",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            };

            const timeOptions = {
              hour: "2-digit",
              minute: "2-digit",
            };

            const formattedTimestamp = note.createdAt
              ? new Intl.DateTimeFormat("en-EN", dateOptions2).format(
                  note.createdAt.toDate()
                )
              : "";

            const formattedLastModification = note.updateAt
              ? new Intl.DateTimeFormat("en-EN", dateOptions2).format(
                  note.updateAt.toDate()
                )
              : "";

            const formattedDate = note.effectiveDateTime
              ? new Intl.DateTimeFormat("en-EN", dateOptions).format(
                  note.effectiveDateTime.toDate()
                )
              : "";

            const formattedTime = note.effectiveDateTime
              ? new Intl.DateTimeFormat("en-EN", timeOptions).format(
                  note.effectiveDateTime.toDate()
                )
              : "";

            const noteDate = formatDate(note.shiftStart, dateOptions);
            const noteTime = formatDate(note.shiftStart, timeOptions); // Assurez-vous que c'est bien le champ que vous voulez utiliser
            const noteDateEnd = formatDate(note.shiftEnd, dateOptions);
            const noteDateEndTime = formatDate(note.shiftEnd, timeOptions);

            switch (note.noteType) {
              case "quickNote":
              case "nonTherapyNote":
              case "therapyNote":
              case "supportStaffNote":
              case "aftercareNote":
                return (
                  <div key={note.id || index} style={{ paddingBottom: "15px" }}>
                    <NotesContainer
                      key={note.id}
                      noteContent={note.noteContent}
                      effectiveDate={note.effectiveDateTime}
                      noteDate={formattedDate}
                      noteTime={formattedTime}
                      doneBy={note.doneBy}
                      timeStamp={formattedTimestamp}
                      lastModification={formattedLastModification}
                      clientPic={note.clientProfilePicture}
                      clientFirstName={note.clientFirstName}
                      clientLastName={note.clientLastName}
                      noteId={note.id}
                      {...note}
                    />
                  </div>
                );

              case "endOfShift":
                return (
                  <div key={note.id || index} style={{ paddingBottom: "15px" }}>
                    <EndOfShiftNoteContainer
                      key={note.id}
                      noteContent={note.noteContent}
                      shiftStartRaw={note.shiftStart}
                      shiftEndRaw={note.shiftEnd}
                      shiftStart={noteDate}
                      shiftEnd={noteDateEnd}
                      staff={note.staff}
                      doneBy={note.doneBy}
                      noteId={note.id}
                      noteType={note.noteType}
                      noteDate={noteDate} // Date formatée
                      noteTime={noteTime} // Heure formatée
                      noteDateEndTime={noteDateEndTime}
                    />
                  </div>
                );

              case "familyNote":
                return (
                  <div key={note.id || index} style={{ paddingBottom: "15px" }}>
                    <FamilyNoteContainer
                      key={note.id}
                      noteContent={note.noteContent}
                      effectiveDate={note.effectiveDateTime}
                      noteDate={formattedDate}
                      noteTime={formattedTime}
                      doneBy={note.doneBy}
                      timeStamp={formattedTimestamp}
                      lastModification={formattedLastModification}
                      clientPic={note.clientProfilePicture}
                      clientFirstName={note.clientFirstName}
                      clientLastName={note.clientLastName}
                      noteId={note.id}
                      relationship={note.relationShipFamilyNote}
                      fileUrls={note.fileUrls}
                      {...note}
                    />
                  </div>
                );
              case "medicalNote":
                return (
                  <div key={note.id || index} style={{ paddingBottom: "15px" }}>
                    <MedicalNoteContainer
                      key={note.id}
                      noteContent={note.noteContent}
                      effectiveDate={note.effectiveDateTime}
                      noteDate={formattedDate}
                      noteTime={formattedTime}
                      doneBy={note.doneBy}
                      timeStamp={formattedTimestamp}
                      lastModification={formattedLastModification}
                      clientPic={note.clientProfilePicture}
                      clientFirstName={note.clientFirstName}
                      clientLastName={note.clientLastName}
                      noteId={note.id}
                      fileUrls={note.fileUrls}
                      {...note}
                    />
                  </div>
                );
              case "handoverNote":
                return (
                  <div key={note.id || index} style={{ paddingBottom: "15px" }}>
                    <HandoverNoteContainer
                      key={note.id}
                      noteContent={note.noteContent}
                      effectiveDateTime={note.effectiveDateTime}
                      noteDate={formattedDate}
                      noteTime={formattedTime}
                      doneBy={note.doneBy}
                      timeStamp={formattedTimestamp}
                      lastModification={formattedLastModification}
                      noteId={note.id}
                      immmigrationAppointment={note.immmigrationAppointment}
                      dischargesSwot={note.dischargesSwot}
                      newClients={note.newClients}
                      medicalInfos={note.medicalInfos}
                      hospitalAppointment={note.hospitalAppointment}
                      residentialClientMovements={
                        note.residentialClientMovements
                      }
                      zoomSessionAssistance={note.zoomSessionAssistance}
                      updateAt={note.updateAt}
                      createdAt={note.createdAt}
                      staffToday={note.staffToday}
                      generalAnnouncement={note.generalAnnouncement}
                      {...note}
                    />
                  </div>
                );

              case "groupNote":
                return (
                  <div key={note.id || index} style={{ paddingBottom: "15px" }}>
                    <GroupNoteContainer
                      key={note.id}
                      noteContent={note.noteContent}
                      effectiveDateTime={note.effectiveDateTime}
                      noteDate={formattedDate}
                      noteTime={formattedTime}
                      doneBy={note.doneBy}
                      timeStamp={formattedTimestamp}
                      lastModification={formattedLastModification}
                      clientPic={note.clientProfilePicture}
                      clientFirstName={note.clientFirstName}
                      clientLastName={note.clientLastName}
                      noteId={note.id}
                      groupTopic={note.groupTopic}
                      groupType={note.typeGroupNote}
                      notInAttendance={note.notInAttendance}
                      updateAt={note.updateAt}
                      createdAt={note.createdAt}
                      groupStartDateTime={note.groupStartDateTime}
                      {...note}
                    />
                  </div>
                );

              default:
                return null;
            }
          })}

          {/* {notesLimit <= filteredNotes.length && ( */}

          <div style={{ display: "flex", flexDirection: "row" }}>
            <div style={{ paddingLeft: "20px" }}>
              <Button
                onClick={() => setNotesLimit((prevLimit) => prevLimit + 10)} // Ajoute 10 notes de plus
                variant="contained"
                color="primary"
              >
                Show More
              </Button>
            </div>
            {/* )} */}
            {notesLimit > 15 && (
              <div style={{ marginTop: "0px", marginLeft: "10px" }}>
                <Button
                  onClick={() => setNotesLimit(15)} // Réinitialise à 15 notes affichées
                  variant="outlined"
                  color="primary"
                  style={{ marginLeft: "10px" }}
                >
                  Show Less
                </Button>
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            width: "5%",
            // backgroundColor: "red",
            display: "flex",
            justifyContent: "flex-start",
            alignContent: "center",
            paddingTop: "30px",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div style={{ textAlign: "center", marginTop: "20px" }}></div>
        </div>
      </div>
      {/* <Clientstab viewMode={viewMode} /> */}
    </div>
  );
}
